import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { AgGridAtom } from "../../../components/atoms/atom";
import ModalAtom from "../../../components/atoms/modal/modal";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";

import NoPermissionAlert from "../../../components/NoPermissionAlert";
import useProjectPermission from "../../../hooks/useProjectPermission";
import INVOICE_STATUS from "../../../constants/invoice-status";

import {
	useDeleteInvoiceMutation,
	useGetProjectInvoicesQuery,
} from "../../../store/services/invoiceService";
import { useGetProjectLVCountQuery } from "../../../store/services/lvService";
import { useGetProjectTicketCategoriesQuery } from "../../../store/services/ticketCategoriesService";
import {
	selectTicketCategoryFilter,
	setTicketCategoryFilter,
} from "../../../store/slices/sharedFilterSlice";

import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { createActionColumn } from "../../../utils/grid-columns";
import {
	getFormattedDate,
	showToast,
} from "../../../utils/method";
import "./project-invoice.scss";
import InvoiceWorkflowSteps from "./components/InvoiceWorkflowSteps";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";
import {
	InvoicePaymentAmountCell,
	SingleInvoiceAmountCell,
} from "./components/InvoiceListAmountCells";
import {
	canDeleteInvoice,
	canNavigateToAufmass,
} from "./utils/invoice-workflow-utils";

const FINALIZED_STATUSES = [
	INVOICE_STATUS.FINALIZED,
];

const isFinalizedInvoiceRow = (status) => FINALIZED_STATUSES.includes(status);

// Column definition factory
const createInvoiceColumns = (t, handlers) => [
	{
		field: "internalReferenceNumber",
		headerName: t("invoice.internal-reference-number"),
		unSortIcon: true,
		maxWidth: 100,
	},
	{
		headerName: t("invoice.ticket-category"),
		field: "projectTicketCategoryId",
		unSortIcon: true,
		valueGetter: (params) =>
			params.data.projectTicketCategory?.name ||
			t("common.not-available"),
	},
	{
		field: "invoiceNumber",
		headerName: t("invoice.invoice-number"),
		unSortIcon: true,
	},
	{
		headerName: t("invoice.billing-period"),
		field: "dateInfo",
		sortable: false,
		minWidth: 160,
		cellRenderer: (params) => {
			const { data } = params;
			const invoiceDate = data.createdAt
				? getFormattedDate(data.createdAt)
				: "–";
			const from = data.billingPeriodFrom
				? getFormattedDate(data.billingPeriodFrom)
				: null;
			const to = data.billingPeriodTo
				? getFormattedDate(data.billingPeriodTo)
				: null;
			const range = from && to ? `${from} – ${to}` : "–";
			return (
				<div className="pi-date-cell">
					<span className="pi-date-cell__range">{range}</span>
					<div className="pi-date-cell__meta">
						<span className="pi-date-cell__meta-label">{t("invoice.invoice-date")}</span>
						<span className="pi-date-cell__meta-value">{invoiceDate}</span>
					</div>
				</div>
			);
		},
	},
	{
		headerName: t("invoice.invoice-amount-net"),
		field: "totalAmount",
		sortable: false,
		type: "numericColumn",
		minWidth: 130,
		width: 140,
		cellRenderer: (params) => (
			<SingleInvoiceAmountCell value={params.data?.totalAmount} />
		),
	},
	{
		headerName: t("invoice.verified-invoice-amount-net"),
		field: "aufmassTotalAmount",
		sortable: false,
		type: "numericColumn",
		minWidth: 145,
		width: 160,
		cellRenderer: (params) => (
			<SingleInvoiceAmountCell value={params.data?.aufmassTotalAmount} />
		),
	},
	{
		headerName: t("invoice.payment-amount-net-gross"),
		field: "paymentAmountGross",
		sortable: false,
		type: "numericColumn",
		minWidth: 155,
		width: 175,
		cellRenderer: (params) => (
			<InvoicePaymentAmountCell data={params.data} t={t} />
		),
	},
	{
		field: "workflowProgress",
		headerName: t("invoice.workflow-progress"),
		sortable: false,
		width: 130,
		maxWidth: 140,
		cellRenderer: (params) => (
			<InvoiceWorkflowSteps
				data={params.data}
				projectCan={params.context?.projectCan}
				onInvoiceClick={handlers.onViewV2}
				onAufmassClick={handlers.onAufmass}
				onSupplierInfoClick={handlers.onSupplierInfo}
			/>
		),
	},
	createActionColumn({
		t,
		actions: [
			{
				projectPermission: { area: PROJECT_AREA.INVOICE, action: PROJECT_ACTION.VIEW },
				onClick: handlers.onViewV2,
				label: "help.action-view-invoice",
			},
			{
				projectPermission: { area: PROJECT_AREA.INVOICE, action: PROJECT_ACTION.MANAGE_AUFMASS },
				onClick: handlers.onAufmass,
				label: (data) =>
					data.hasAufmass
						? "help.action-view-aufmass"
						: "help.action-create-aufmass",
				show: (data) => canNavigateToAufmass(data),
			},
			{
				projectPermission: {
					area: PROJECT_AREA.INVOICE,
					action: PROJECT_ACTION.MANAGE_SUPPLIER_INFO,
				},
				onClick: handlers.onSupplierInfo,
				label: "help.action-view-supplier-info",
				show: (data) => data.hasSupplierInfo,
			},
			{
				projectPermission: { area: PROJECT_AREA.INVOICE, action: PROJECT_ACTION.MANAGE_SUPPLIER_INFO },
				onClick: handlers.onSupplierInfo,
				label: "help.action-create-supplier-info",
				show: (data) => !data.hasSupplierInfo,
			},
			{
				projectPermission: { area: PROJECT_AREA.INVOICE, action: PROJECT_ACTION.DELETE },
				onClick: handlers.onDelete,
				label: "help.action-delete",
				danger: true,
				show: (data) => canDeleteInvoice(data),
			},
		],
	}),
];

// Custom hooks
const useFilterConfiguration = (
	ticketCategories,
	t,
	selectedTicketCategoryId,
	hasLVs,
	lvCountLoading,
	canCreateInvoice,
) => {
	return useMemo(() => {
		let disabledReason = "";
		if (lvCountLoading) {
			disabledReason = "invoice.lv.loading";
		} else if (!hasLVs) {
			disabledReason = "invoice.error-no-lv-available";
		}

		return getSearchAddFilter(
			canCreateInvoice
				? {
						addTextKey: "invoice.add-invoice",
						addDisabled: !hasLVs || lvCountLoading,
						addDisabledReason: disabledReason,
					}
				: { hideAddButton: true },
			[
				{
					key: "ticketCategory",
					type: "dropdown",
					labelKey: "invoice.select-ticket-category",
					options:
						ticketCategories?.map((cat) => ({
							value: cat.id,
							label: cat.name,
						})) || [],
					value: selectedTicketCategoryId || "",
				},
				{
					key: "status",
					type: "dropdown",
					labelKey: "invoice.status.select-status",
					sortOptions: false,
					shouldTranslate: true,
					options: Object.values(INVOICE_STATUS).map((status) => ({
						value: status,
						label: `invoice.status.${status}`,
					})),
					value: "",
				},
			],
		);
	}, [
		ticketCategories,
		selectedTicketCategoryId,
		hasLVs,
		lvCountLoading,
		canCreateInvoice,
	]);
};

// Main Component
function ProjectInvoicePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Redux state
	const projectDetails = useSelector(selectProjectDetails);
	const projectId = projectDetails?.id;

	const { data: categoriesResponse } = useGetProjectTicketCategoriesQuery(
		{ projectId },
		{ skip: !projectId },
	);
	const ticketCategories = useMemo(
		() => categoriesResponse?.items ?? [],
		[categoriesResponse],
	);
	const {
		data: lvCountResponse,
		isLoading: lvCountLoading,
	} = useGetProjectLVCountQuery(projectId, { skip: !projectId });
	const hasLVs = lvCountResponse?.hasLVs ?? false;
	const {
		can,
		isLoading: permissionLoading,
		filterByCategoryScope,
	} = useProjectPermission();
	const canViewInvoice = !permissionLoading && can(PROJECT_AREA.INVOICE, PROJECT_ACTION.VIEW);
	const canCreateInvoice = can(PROJECT_AREA.INVOICE, PROJECT_ACTION.CREATE);

	// Use a custom selector with projectId
	const selectedTicketCategoryId =
		useSelector((state) => selectTicketCategoryFilter(state, projectId)) ||
		"";

	const [page, setPage] = useState(1);
	const [queryFilters, setQueryFilters] = useState(() => ({
		search: "",
		ticketCategoryId: selectedTicketCategoryId || "",
		status: "",
	}));

	const {
		data: invoicesResponse,
		isFetching: isInvoicesFetching,
	} = useGetProjectInvoicesQuery(
		{ projectId, page, limit: 50, ...queryFilters },
		{ skip: !projectId || !canViewInvoice },
	);
	const [deleteInvoice] = useDeleteInvoiceMutation();

	const invoiceList = useMemo(
		() => invoicesResponse?.items ?? [],
		[invoicesResponse],
	);
	const invoiceHasMore = page < (invoicesResponse?.after?.totalPages ?? 0);
	const loading = { invoices: isInvoicesFetching };

	const scopedTicketCategories = useMemo(
		() => filterByCategoryScope(ticketCategories, (item) => item.id),
		[filterByCategoryScope, ticketCategories],
	);
	const scopedInvoiceList = useMemo(
		() =>
			filterByCategoryScope(
				invoiceList || [],
				(item) =>
					item.projectTicketCategoryId ??
					item.projectTicketCategory?.id,
			),
		[filterByCategoryScope, invoiceList],
	);

	// Local state
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [invoiceToDelete, setInvoiceToDelete] = useState(null);

	// Custom hooks - call these at the top level
	const filterConfiguration = useFilterConfiguration(
		scopedTicketCategories,
		t,
		selectedTicketCategoryId,
		hasLVs,
		lvCountLoading,
		canCreateInvoice,
	);
	const [filterItems, setFilterItems] = useState(filterConfiguration);

	// Update filter items when configuration changes
	useEffect(() => {
		setFilterItems(filterConfiguration);
	}, [filterConfiguration]);

	// Event handlers
	const handleDelete = useCallback((data) => {
		setInvoiceToDelete(data);
		setShowDeleteModal(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!invoiceToDelete) return;

		try {
			await deleteInvoice({
				projectId,
				invoiceId: invoiceToDelete.id,
			}).unwrap();
			showToast(t("invoice.invoice-delete-success"), { type: "success" });
		} catch (error) {
			showToast(t(error), { type: "error" });
		} finally {
			setShowDeleteModal(false);
			setInvoiceToDelete(null);
		}
	}, [deleteInvoice, projectId, invoiceToDelete, t]);

	const handleView = useCallback(
		(data) => navigate(`view/${data.id}`),
		[navigate],
	);

	const handleAufmass = useCallback(
		(data) => navigate(`/projects/${projectId}/invoice/aufmass-v2?invoiceId=${data.id}`),
		[navigate, projectId],
	);

	const handleEdit = useCallback(
		(data) => navigate(`edit/${data.id}`),
		[navigate],
	);

	const handleSupplierInfo = useCallback(
		(data) => navigate(`supplier-info/${data.id}`),
		[navigate],
	);

	const handleEditV2 = useCallback(
		(data) => navigate(`/projects/${projectId}/invoice-v2/edit/${data.id}`),
		[navigate, projectId],
	);

	const handleViewV2 = useCallback(
		(data) => navigate(`/projects/${projectId}/invoice-v2/view/${data.id}`),
		[navigate, projectId],
	);

	// Memoized values
	const actionHandlers = useMemo(
		() => ({
			onDelete: handleDelete,
			onView: handleView,
			onAufmass: handleAufmass,
			onEdit: handleEdit,
			onSupplierInfo: handleSupplierInfo,
			onEditV2: handleEditV2,
			onViewV2: handleViewV2,
		}),
		[
			handleDelete,
			handleView,
			handleAufmass,
			handleEdit,
			handleSupplierInfo,
			handleEditV2,
			handleViewV2,
		],
	);

	const invoiceColumns = useMemo(
		() => createInvoiceColumns(t, actionHandlers),
		[t, actionHandlers],
	);

	const onFilterAction = useCallback(
		(index, key, info) => {
			if (key === "add") {
				navigate(`/projects/${projectId}/invoice-v2`);
				return;
			}

			if (key === "back") {
				navigate("/projects");
				return;
			}

			// Update filter items
			const updatedItems = [...filterItems];
			updatedItems[index].value = info?.value || "";
			setFilterItems(updatedItems);

			// Persist ticket category to shared Redux state
			if (key === "ticketCategory") {
				dispatch(
					setTicketCategoryFilter({
						projectId,
						ticketCategoryId: info?.value || "",
					}),
				);
			}

			// Derive new filter values from the just-updated items
			const search =
				updatedItems.find((i) => i.key === "search")?.value || "";
			const ticketCategoryId =
				updatedItems.find((i) => i.key === "ticketCategory")?.value ||
				"";
			const status =
				updatedItems.find((i) => i.key === "status")?.value || "";

			setQueryFilters({ search, ticketCategoryId, status });
			setPage(1);
		},
		[filterItems, navigate, dispatch, projectId],
	);

	const getInvoiceRowClass = useCallback((params) => {
		const status = params.data?.status;
		if (status === INVOICE_STATUS.REJECTED) return "pi-row--rejected";
		if (isFinalizedInvoiceRow(status)) return "pi-row--finalized";
		return "pi-row--in-progress";
	}, []);

	const gridOptions = useMemo(
		() => ({
			flex: 1,
			sortable: true,
			wrapText: false,
			autoHeight: false,
		}),
		[],
	);

	const onGridBodyScrollEnd = useCallback(
		(event) => {
			if (!invoiceHasMore || loading?.invoices) return;
			const { api } = event;
			const lastDisplayed = api.getLastDisplayedRowIndex();
			const totalDisplayed = api.getDisplayedRowCount();
			if (lastDisplayed >= totalDisplayed - 3) {
				setPage((p) => p + 1);
			}
		},
		[invoiceHasMore, loading?.invoices],
	);

	if (permissionLoading) {
		return (
			<div className="project-invoice-page">
				<ProjectHeader projectName="" tabTitle={t("invoice.title")} />
				<div className="page-container">
					<TableSkeleton />
				</div>
			</div>
		);
	}

	if (!canViewInvoice) {
		return (
			<div className="project-invoice-page">
				<ProjectHeader
					projectName={projectDetails?.name || ""}
					tabTitle={t("invoice.title")}
				/>
				<NoPermissionAlert />
			</div>
		);
	}

	// Loading state
	if (!projectDetails) {
		return (
			<div className="project-invoice-page">
				<ProjectHeader projectName="" tabTitle={t("invoice.title")} />
				<div className="page-container">
					<div className="flex-grow-1 ag-grid-parent">
						<TableSkeleton />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="project-invoice-page">
			<ProjectHeader
				projectName={projectDetails.name}
				tabTitle={t("invoice.title")}
			/>

			<div className="page-container">
				<FilterBar
					items={filterItems}
					onFilterAction={onFilterAction}
					canAdd={canCreateInvoice}
				/>

				<div className="flex-grow-1 ag-grid-parent position-relative">
					{loading?.invoices && invoiceList.length === 0 ? (
						<TableSkeleton />
					) : (
						<AgGridAtom
							columnDefs={invoiceColumns}
							rowData={scopedInvoiceList}
							context={{ projectCan: can }}
							overlayLoadingTemplate=""
							overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t("invoice.no-invoices")}</span>`}
							defaultColDef={gridOptions}
							rowHeight={60}
							suppressRowTransform={true}
							getRowClass={getInvoiceRowClass}
							getRowId={(params) => params.data.id}
							onBodyScrollEnd={onGridBodyScrollEnd}
						/>
					)}

					{loading?.invoices && invoiceList.length > 0 && (
						<div className="position-absolute bottom-0 start-50 translate-middle-x pb-2 pi-pagination-spinner">
							<div className="spinner-border spinner-border-sm text-primary" role="status">
								<span className="visually-hidden">{t("common.loading")}</span>
							</div>
						</div>
					)}
				</div>

				<ModalAtom
					title={t("invoice.delete-invoice")}
					body={<p>{t("invoice.confirm-delete-invoice")}</p>}
					saveText={t("delete")}
					show={showDeleteModal}
					handleClose={() => {
						setShowDeleteModal(false);
						setInvoiceToDelete(null);
					}}
					handleSubmit={confirmDelete}
				/>
			</div>
		</div>
	);
}

export default ProjectInvoicePage;
